import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Image,
  useBreakpointValue
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Trust Badges Section
 * Certifications, credentials, and trust signals
 */
const TrustBadges = ({ data }) => {
  const gridCols = useBreakpointValue({ base: 2, md: 3, lg: 4 });

  if (!data) return null;

  const {
    title = "Trusted By",
    subtitle,
    badges = []
  } = data;

  if (badges.length === 0) return null;

  return (
    <Box py={{ base: 12, md: 16 }} bg="white" borderTop="1px solid" borderColor="gray.200">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          mb={{ base: 8, md: 12 }}
        >
          <VStack align="center" spacing={2} textAlign="center">
            {title && (
              <Heading as="h3" size="md" fontWeight="700" color="gray.900">
                {title}
              </Heading>
            )}
            {subtitle && (
              <Text fontSize="sm" color="gray.600">
                {subtitle}
              </Text>
            )}
          </VStack>
        </MotionBox>

        {/* Badges Grid */}
        <SimpleGrid columns={gridCols} spacing={{ base: 6, md: 8 }}>
          {badges.map((badge, idx) => (
            <MotionBox
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05, duration: 0.4 }}
            >
              <VStack
                align="center"
                justify="center"
                spacing={2}
                p={4}
                borderRadius="lg"
                bg="gray.50"
                h="full"
                transition="all 0.2s"
                _hover={{ bg: 'gray.100', transform: 'translateY(-4px)' }}
              >
                {badge.image ? (
                  <Image
                    src={badge.image}
                    alt={badge.title}
                    maxH="60px"
                    maxW="100%"
                    objectFit="contain"
                  />
                ) : (
                  <Box fontSize="2xl">{badge.icon || '✓'}</Box>
                )}
                {badge.title && (
                  <Text fontSize="sm" fontWeight="600" color="gray.900" textAlign="center">
                    {badge.title}
                  </Text>
                )}
                {badge.description && (
                  <Text fontSize="xs" color="gray.600" textAlign="center">
                    {badge.description}
                  </Text>
                )}
              </VStack>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default TrustBadges;
